import React, { useRef, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import { useEventTracking, useOrganizationArchivedUsers } from '@hooks';
import { ClickTarget, ContentType, TrackingLocation, TypingInput, click, typing } from '@tracking';

import SectionLayout from '../../SectionLayout';
import { GroupUser, UserOperationType, UserType } from '../../types';
import PaginatedTable from '../UsersTable/PaginatedTable';
import SearchBar from './SearchBar';

interface ArchivedUsersProps {
  onUserClick: (user: GroupUser, operation: UserOperationType) => void;
}

const ArchivedUsers = ({ onUserClick }: ArchivedUsersProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const result = useOrganizationArchivedUsers(debouncedSearchTerm);
  const hasTrackedTyping = useRef(false);
  const { trackEvent, Track } = useEventTracking({
    location: TrackingLocation.ArchivedUsers,
  });

  const handleUserClick = (user: GroupUser, operation: UserOperationType) => {
    onUserClick(user, operation);
    trackEvent(
      click({
        clickTarget: ClickTarget.UserRow,
        contentId: user.id,
        contentType: ContentType.User,
      })
    );
  };

  const handleSearchTyping = (term: string) => {
    setSearchTerm(term);
    if (!hasTrackedTyping.current && term.length > 0) {
      hasTrackedTyping.current = true;
      trackEvent(
        typing({
          inputTarget: TypingInput.SearchBar,
        })
      );
    }
  };

  return (
    <Track>
      <SectionLayout
        id="archived"
        title="Archived users"
        note="Click on a user below to reactivate"
        sectionId="archived-users"
        leftElement={<SearchBar searchTerm={searchTerm} setSearchTerm={handleSearchTyping} />}
      >
        <PaginatedTable {...result} userType={UserType.Archived} onUserClick={handleUserClick} />
      </SectionLayout>
    </Track>
  );
};

export default ArchivedUsers;
